import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import moment from "moment";
import { Oval } from "react-loader-spinner";
import { copyToClipBoard, formatCurrencyValue, showToast } from "../../helper-methods";
import { getSwapDetails } from "../../http/http-calls";

const SwapDetailsModal = ({ isOpen, toggle, swapData }) => {
  const [detailedData, setDetailedData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchSwapDetails = useCallback(async () => {
    if (!swapData?.id) return;
    try {
      setIsLoading(true);
      const response = await getSwapDetails(swapData.id);
      // Backend wraps response in {error: false, data: {...}}
      setDetailedData(response.data || response);
    } catch (error) {
      showToast(error?.reason || error?.message || "Error fetching swap details", "error");
    } finally {
      setIsLoading(false);
    }
  }, [swapData?.id]);

  useEffect(() => {
    if (isOpen && swapData?.id) {
      fetchSwapDetails();
    }
  }, [isOpen, swapData?.id, fetchSwapDetails]);

  const _closeModal = () => {
    setDetailedData(null);
    toggle();
  };

  const displayData = detailedData || swapData;

  const swapLink = useMemo(() => {
    if (!displayData?.artist?.publicSlug || !displayData?.shortId) {
      return "";
    }
    return `https://app.grouped.com/${displayData.artist.publicSlug}/s/${displayData.shortId}`;
  }, [displayData?.artist?.publicSlug, displayData?.shortId]);

  const handleCopyLink = () => {
    copyToClipBoard("copy", swapLink);
  };

  // Calculate traffic source percentages
  const totalViews = displayData?.trafficSources?.reduce((sum, s) => sum + s.views, 0) || 0;
  const trafficSourcesWithPercentage = displayData?.trafficSources?.map((source) => {
    const percentage = totalViews > 0 ? ((source.views / totalViews) * 100).toFixed(1) : 0;
    return { ...source, percentage };
  }) || [];

  return (
    <Modal
      size="lg"
      isOpen={isOpen}
      toggle={() => _closeModal()}
      scrollable
      centered
      className="swapDetailsModal"
    >
      <ModalHeader toggle={() => _closeModal()}>
        {displayData?.title || "Swap Details"}
      </ModalHeader>
      <ModalBody>
        {isLoading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <Oval height="50" width="50" color="#3498DB" />
          </div>
        ) : (
          <>
            {/* Description */}
            {displayData?.description && (
              <p className="mb-3">{displayData.description}</p>
            )}

            {/* Swap Link Section */}
            {swapLink && (
              <div className="swapLinkSection mb-3">
                <label className="mb-2">
                  <strong>Swap Link</strong>
                </label>
                <InputGroup>
                  <Input
                    type="text"
                    value={swapLink}
                    readOnly
                    className="bg-light"
                  />
                  <InputGroupText
                    onClick={handleCopyLink}
                    className="cursor-pointer"
                  >
                    <i className="fa fa-copy mx-2" /> Copy
                  </InputGroupText>
                </InputGroup>
              </div>
            )}

            {/* Artist Section */}
            {displayData?.artist && (
              <div className="artistSection mb-3">
                <label className="mb-2">
                  <strong>Artist</strong>
                </label>
                <div className="d-flex align-items-center p-3 bg-light rounded">
                  {displayData.artist.displayPictureUrls?.default && (
                    <img
                      src={displayData.artist.displayPictureUrls.default}
                      alt={displayData.artist.name}
                      className="rounded-circle me-3 avatar-md"
                    />
                  )}
                  <div>
                    <h6 className="mb-0">{displayData.artist.name}</h6>
                    <p className="mb-0 text-muted fs-14">
                      {displayData.artist.email}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Performance Metrics */}
            {displayData?.metrics && (
              <div className="performanceSection mb-3">
                <label className="mb-2">
                  <strong>Performance</strong>
                </label>
                <div className="row">
                  <div className="col-6 mb-3">
                    <div className="p-3 bg-light rounded text-center">
                      <p className="mb-1 text-muted fs-14">
                        Views
                      </p>
                      <h4 className="mb-0">
                        {displayData.metrics.views?.toLocaleString() || 0}
                      </h4>
                    </div>
                  </div>
                  <div className="col-6 mb-3">
                    <div className="p-3 bg-light rounded text-center">
                      <p className="mb-1 text-muted fs-14">
                        Signups
                      </p>
                      <h4 className="mb-0">
                        {displayData.metrics.signups?.toLocaleString() || 0}
                      </h4>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="p-3 bg-light rounded text-center">
                      <p className="mb-1 text-muted fs-14">
                        Conversion
                      </p>
                      <h4 className="mb-0">
                        {displayData.metrics.conversionRate
                          ? `${(displayData.metrics.conversionRate * 100).toFixed(1)}%`
                          : "0%"}
                      </h4>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="p-3 bg-light rounded text-center">
                      <p className="mb-1 text-muted fs-14">
                        Revenue
                      </p>
                      <h4 className="mb-0">
                        {displayData.metrics.revenue
                          ? formatCurrencyValue(displayData.metrics.revenue)
                          : "—"}
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Top Traffic Sources */}
            {trafficSourcesWithPercentage.length > 0 && (
              <div className="trafficSourcesSection mb-4">
                <label className="mb-2">
                  <strong>Top Traffic Sources</strong>
                </label>
                {trafficSourcesWithPercentage.map((source, index) => (
                  <div key={index} className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span>{source.name}</span>
                      <span className="text-muted">
                        {source.views.toLocaleString()} views ({source.percentage}%)
                      </span>
                    </div>
                    <div className="progress" style={{ height: "8px" }}>
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${source.percentage}%` }}
                        aria-valuenow={source.percentage}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Dates Section */}
            <div className="datesSection">
              <div className="row">
                <div className="col-6">
                  <p className="mb-1 text-muted text-uppercase fs-12">
                    Created
                  </p>
                  <div>{moment(displayData?.createdAt).format("MMM DD, YYYY")}</div>
                </div>
                <div className="col-6">
                  <p className="mb-1 text-muted text-uppercase fs-12">
                    Last Updated
                  </p>
                  <div>
                    {moment(displayData?.updatedAt).format("MMM DD, YYYY")}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="link" className="closeBtn" onClick={() => _closeModal()}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SwapDetailsModal;
